import { useEffect } from 'react';
import { throttle } from 'lodash';

import { capture } from '@/analytics';

// 30m: inactivity gap that ends a session and starts a new one (matches PostHog's default).
const SESSION_IDLE_MS = 30 * 60 * 1000;
// 60s: cap on how often activity is re-evaluated and persisted, so the recorded gap is at most this stale.
const ACTIVITY_THROTTLE_MS = 60 * 1000;
const LAST_ACTIVITY_KEY = 'session_last_activity_at';

/**
 * Fires `session_started` on the first activity of a new session, where a session begins after
 * 30+ minutes of inactivity. `since_last_session_minutes` is the true idle gap, so `last_activity_at`
 * is refreshed as the user stays active; the leading-edge throttle caps writes at once per minute.
 */
export const useSessionStart = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;

    const evaluate = throttle(
      () => {
        const now = Date.now();
        const lastActivityAt = readLastActivity();

        if (lastActivityAt === null) {
          capture('session_started', {});
        } else if (now - lastActivityAt > SESSION_IDLE_MS) {
          capture('session_started', { since_last_session_minutes: Math.round((now - lastActivityAt) / 60_000) });
        }

        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      },
      ACTIVITY_THROTTLE_MS,
      { leading: true, trailing: false }
    );

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') evaluate();
    };

    evaluate();

    window.addEventListener('pointerdown', evaluate);
    window.addEventListener('keydown', evaluate);
    window.addEventListener('scroll', evaluate, { capture: true, passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      evaluate.cancel();
      window.removeEventListener('pointerdown', evaluate);
      window.removeEventListener('keydown', evaluate);
      window.removeEventListener('scroll', evaluate, { capture: true });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);
};

const readLastActivity = (): number | null => {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};
